import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const OurServices = ({ service }) => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 60 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: "easeOut" }
        }
    };
    const [activeIndex, setActiveIndex] = useState(0);

    // Sirf mobile view detect karne ke liye state (taaki desktop untouch rahe)
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        handleResize(); // Initial check
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const nextSlide = () => setActiveIndex((prev) => (prev + 1) % service.length);
    const prevSlide = () => setActiveIndex((prev) => (prev - 1 + service.length) % service.length);

    return (
        <section className="relative w-full lg:pb-[100px] pb-[60px] bg-white overflow-hidden font-sans">
            <div className="max-w-[1440px] mx-auto px-[15px] lg:px-[80px] relative z-10">
                <div className="mb-[30px] lg:mb-[80px]">
                    <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] relative z-10">
                        Service Offerings
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-[30px] lg:gap-10 items-start lg:items-center">
                    <div className="lg:col-span-3 flex flex-col items-center justify-center relative h-[300px] lg:h-[550px]">

                        {/* --- MAIN CENTER CIRCLE --- */}
                        <motion.div
                            className="w-[80px] h-[80px] lg:w-[220px] lg:h-[220px] bg-[#01386A] rounded-full absolute z-0 shadow-lg shadow-[#01386A]/30"
                            animate={{
                                // Mobile par kam movement taaki container se bahar na nikle, Desktop pe original
                                y: isMobile ? [-40, 40, -40] : [-100, 100, -100],
                            }}
                            transition={{
                                duration: 7,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        />

                        {/* --- OUTER RINGS --- */}
                        {/* Mobile pe center karne ke liye 'w-full h-full justify-center', Desktop ke liye aapki original properties ('lg:flex-col lg:h-auto') */}
                        <div className="relative z-10 flex items-center justify-center w-full h-full lg:flex-col lg:h-auto lg:w-auto">
                            {[...Array(4)].map((_, idx) => (
                                <motion.div
                                    key={idx}
                                    // Mobile pe rings ko exact center overlap karne ke liye absolute, desktop pe original relative + margin setup
                                    className="border border-[#01386A]/40 rounded-full absolute lg:relative lg:mb-[-120px]"
                                    style={{
                                        width: `calc(120px + ${idx * 5}px)`,
                                        height: `calc(120px + ${idx * 5}px)`
                                    }}
                                    animate={{
                                        scale: activeIndex === idx ? 1.05 : 1,
                                        borderColor: activeIndex === idx ? "#01386A" : "rgba(1, 56, 106, 0.4)",
                                        // Mobile me rings ka size clearly dikhane ke liye calculation, Desktop pe original
                                        width: !isMobile ? `${180 + (idx * 5)}px` : `${140 + (idx * 20)}px`,
                                        height: !isMobile ? `${180 + (idx * 5)}px` : `${140 + (idx * 20)}px`
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-4 h-full w-full">
                        <div className="bg-[#F0F0F0] p-[24px] lg:p-[36px] h-full flex flex-col justify-between min-h-[300px] lg:min-h-[460px] relative overflow-hidden shadow-sm">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeIndex}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.4 }}
                                    className="flex flex-col h-full"
                                >
                                    <span className="text-[#01386A] text-[16px] lg:text-[20px] font-sora font-[600] leading-[21.6px] mb-2">
                                        {activeIndex + 1}/
                                    </span>
                                    <h3 className="text-[#01386A] texttext-[16px] lg:text-[20px] font-sora font-[600] mb-[15px] lg:mb-[25px]">
                                        {service[activeIndex].title}
                                    </h3>
                                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-graphik font-[500] leading-[1.6] lg:leading-[1.7] mb-auto">
                                        {service[activeIndex].desc}
                                    </p>
                                </motion.div>
                            </AnimatePresence>

                            <div className="flex gap-[12px] mt-[20px] lg:mt-8">
                                <button onClick={prevSlide} className="w-[32px] h-[32px] rounded-full border border-gray-200 flex items-center justify-center hover:bg-[#ED5800] hover:text-white transition-all text-[#102B4F] hover:border-[#ED5800]">
                                    <ArrowLeft size={12} />
                                </button>
                                <button onClick={nextSlide} className="w-[32px] h-[32px] rounded-full border border-gray-200 flex items-center justify-center hover:bg-[#ED5800] hover:text-white transition-all text-[#102B4F] hover:border-[#ED5800]">
                                    <ArrowRight size={12} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-5 flex flex-col justify-center mt-[10px] lg:mt-0 w-full">
                        {service.map((item, idx) => (
                            <div
                                key={idx}
                                onMouseEnter={() => setActiveIndex(idx)}
                                onClick={() => setActiveIndex(idx)}
                                className={`group cursor-pointer py-[15px] lg:py-6 relative border-b ${activeIndex === idx ? 'border-[#01386A]' : 'border-gray-400'}`}
                            >
                                <div className="flex items-center gap-[15px] lg:gap-6">
                                    <span className={`text-[16px] lg:text-[20px] font-sora font-[600] transition-colors duration-300 ${activeIndex === idx ? 'text-[#01386A]' : 'text-gray-400'}`}>
                                        {idx + 1}/
                                    </span>
                                    <h4 className={`text-[16px] lg:text-[20px] font-sora font-[600] transition-colors duration-300 ${activeIndex === idx ? 'text-[#01386A]' : 'text-gray-300 group-hover:text-gray-500'}`}>
                                        {item.title}
                                    </h4>
                                </div>

                                <div className="absolute bottom-0 left-0 w-full h-[1px] bg-transparent overflow-hidden">
                                    <motion.div
                                        initial={{ x: "-100%" }}
                                        animate={{ x: activeIndex === idx ? "0%" : "-100%" }}
                                        transition={{ duration: 0.5 }}
                                        className="w-full h-full bg-[#01386A]"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </section>
    );
};

export default OurServices;